// src/components/curator/CuratorDecisionCard.tsx
//
// Single card rendering one curator decision in the Memory log.
// Designed for fast inspection: at-a-glance you can see the
// decision (color-coded), what triggered it, and what the curator
// wrote (if anything). Tap to expand for the full rationale,
// candidate text, and provenance details.
//
// Discards are rendered with muted styling to deprioritize them
// visually — they're the most common decision and don't carry
// new memory writes — but they ARE included so the user can see
// what the curator chose NOT to remember and why. That's often
// the most useful diagnostic signal.

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import type { CuratorDecisionRow } from '../../hooks/useCuratorDecisions';
import { useT } from '../../i18n/useT';

interface Props {
  decision: CuratorDecisionRow;
}

/** Color scheme per CURATOR_NOTES.md "The Curator Log UI" section.
 *  We use the theme palette plus a few custom tints (yellow/orange)
 *  that don't exist as MUI semantic colors. */
function decisionStyle(decision: CuratorDecisionRow['decision']): {
  label: string;
  color: string;
  background: string;
} {
  switch (decision) {
    case 'create':
      return { label: 'create', color: '#1565c0', background: '#e3f2fd' };
    case 'merge':
      return { label: 'merge', color: '#6a1b9a', background: '#f3e5f5' };
    case 'supersede':
      return { label: 'supersede', color: '#bf360c', background: '#fbe9e7' };
    case 'annotate':
      return { label: 'annotate', color: '#2e7d32', background: '#e8f5e9' };
    case 'promote_pending':
      return { label: 'promote pending', color: '#bf6f00', background: '#fff8e1' };
    case 'discard':
    default:
      return { label: 'discard', color: '#616161', background: '#f5f5f5' };
  }
}

function triggerLabel(kind: CuratorDecisionRow['trigger_kind']): string {
  switch (kind) {
    case 'chat_turn':
      return 'chat';
    case 'photo_annotation':
      return 'photo';
    case 'heartbeat':
      return 'location';
    case 'user_statement':
      return 'statement';
    case 'profile_seed':
      return 'profile';
    case 'message_bookmark':
      return 'bookmark';
    case 'guide_card_heart':
      return 'heart';
    case 'taste_consolidation':
      return 'taste';
    default:
      return kind;
  }
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return '';
  const now = Date.now();
  const ms = now - date.getTime();
  if (ms < 0) return 'just now';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 30) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

export const CuratorDecisionCard: React.FC<Props> = ({ decision }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const t = useT();
  const style = decisionStyle(decision.decision);
  const isDiscard = decision.decision === 'discard';

  // Discards use muted tone so the eye skips past them when
  // scanning for write events. Other decisions use full contrast.
  const cardOpacity = isDiscard ? 0.65 : 1.0;

  // Headline text — what the curator wrote, or for discards/
  // annotates, a short summary of what was considered. The
  // candidate_text is the user's original message; final_text is
  // the curator's rewrite. For discards there's no final_text
  // so we show the candidate as "considered: …".
  const headline = decision.final_text?.trim()
    ? decision.final_text
    : decision.candidate_text?.trim()
      ? `considered: ${decision.candidate_text.slice(0, 200)}`
      : '(no candidate text)';

  return (
    <Box
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderLeft: `3px solid ${style.color}`,
        borderRadius: 1,
        backgroundColor: alpha(style.background, 0.4),
        p: 2,
        opacity: cardOpacity,
        transition: 'opacity 120ms',
      }}
    >
      {/* Header row: timestamp · trigger · decision badge */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.5, flexWrap: 'wrap' }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontFeatureSettings: '"tnum"' }}
        >
          {formatRelativeTime(decision.triggered_at)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ·
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {triggerLabel(decision.trigger_kind)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={style.label}
          size="small"
          sx={{
            backgroundColor: style.background,
            color: style.color,
            fontWeight: 600,
            height: 22,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 1 },
          }}
        />
        {decision.half_life_hint && decision.decision !== 'discard' && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {decision.half_life_hint}
          </Typography>
        )}
      </Stack>

      {/* Headline text */}
      <Typography
        variant="body2"
        sx={{
          fontStyle: isDiscard ? 'italic' : 'normal',
          mb: decision.rationale ? 0.5 : 0,
        }}
      >
        {headline}
      </Typography>

      {/* Rationale — the curator's explanation */}
      {decision.rationale && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            mt: 0.5,
            lineHeight: 1.4,
          }}
        >
          {decision.rationale}
        </Typography>
      )}

      {/* Expand toggle — only render when there's something to expand */}
      {(decision.evidence_quote ||
        decision.uncertainties.length > 0 ||
        decision.retrieved_neighbor_count > 0 ||
        decision.curator_run_status === 'quote_unverified_downgraded' ||
        decision.curator_run_status === 'parse_failed' ||
        decision.curator_run_status === 'schema_invalid') && (
        <>
          <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              sx={{ p: 0.5 }}
            >
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {expanded ? 'hide details' : 'details'}
            </Typography>
          </Stack>
          <Collapse in={expanded}>
            <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${alpha(theme.palette.divider, 0.5)}` }}>
              {decision.evidence_quote && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {t('evidence quote (verbatim):')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    "{decision.evidence_quote}"
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {decision.source_kind && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('source:')} <code>{decision.source_kind}</code>
                  </Typography>
                )}
                {decision.retrieved_neighbor_count > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('{n} neighbor(s) considered', { n: decision.retrieved_neighbor_count })}
                  </Typography>
                )}
                {decision.applied_writes_count > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('{n} write(s)', { n: decision.applied_writes_count })}
                  </Typography>
                )}
                {decision.duration_ms !== null && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {(decision.duration_ms / 1000).toFixed(1)}s
                  </Typography>
                )}
              </Stack>

              {decision.uncertainties.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {t('uncertainties:')}
                  </Typography>
                  {decision.uncertainties.map((u, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{ display: 'block', pl: 1, color: 'text.secondary' }}
                    >
                      · {u}
                    </Typography>
                  ))}
                </Box>
              )}

              {decision.curator_run_status &&
                decision.curator_run_status !== 'ok' && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'warning.main',
                        fontWeight: 500,
                      }}
                    >
                      {t('curator status:')} {decision.curator_run_status}
                    </Typography>
                    {decision.curator_run_status ===
                      'quote_unverified_downgraded' && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('Provenance discipline: explicit_statement claim could not be verified against the source message; downgraded to behavioral_inference.')}
                      </Typography>
                    )}
                  </Box>
                )}

              {decision.curator_model && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, color: 'text.disabled', fontSize: '0.65rem' }}
                >
                  {decision.curator_model}
                </Typography>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};
